import { apiClient } from '../utils/api';

// ── Types ─────────────────────────────────────────────────────────────────────

export type TestCasePriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type TestCaseStatus = 'DRAFT' | 'ACTIVE' | 'DEPRECATED';
export type TestInstanceStatus = 'NOT_RUN' | 'IN_PROGRESS' | 'PASSED' | 'FAILED' | 'PARTIAL' | 'BLOCKED';
export type TestEnvironment = 'LOCAL' | 'STAGING' | 'PRODUCTION';
export type StepResultStatus = 'passed' | 'failed' | 'skipped' | 'not_run';
export type IssueSeverity = 'blocker' | 'major' | 'minor' | 'cosmetic';

export interface TestStep {
  order: number;
  action: string;
  expectedResult: string;
}

export interface TestDataSet {
  id: string;
  label: string;
  data: Record<string, unknown>;
}

export interface TestCase {
  id: string;
  moduleId: string;
  externalRef?: string | null;
  name: string;
  description?: string | null;
  priority: TestCasePriority;
  status: TestCaseStatus;
  primaryActor: string;
  secondaryActors: string[];
  preconditions: string[];
  postconditions: string[];
  steps: TestStep[];
  testData: TestDataSet[];
  relatedTestCases: string[];
  tags: string[];
  createdByAdminId?: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { instances: number };
}

export interface StepResult {
  stepOrder: number;
  status: StepResultStatus;
  notes?: string | null;
}

export interface Issue {
  stepOrder?: number | null;
  severity: IssueSeverity;
  description: string;
  jiraTicket?: string | null;
  markerIoReportId?: string | null;
}

export interface TestCaseInstance {
  id: string;
  testCaseId: string;
  testCase?: Pick<TestCase, 'id' | 'name' | 'moduleId' | 'primaryActor'>;
  executedByAdminId?: string | null;
  executorName?: string | null;
  executorRole?: string | null;
  date?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  environment: TestEnvironment;
  status: TestInstanceStatus;
  stepResults: StepResult[];
  issues: Issue[];
  generalNotes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TestCaseListQuery {
  moduleId?: string;
  status?: TestCaseStatus;
  priority?: TestCasePriority;
  primaryActor?: string;
  tag?: string;
  search?: string;
}

export interface InstanceListQuery {
  testCaseId?: string;
  status?: TestInstanceStatus;
  environment?: TestEnvironment;
}

export interface ImportResult {
  imported: number;
  updated: number;
  skipped: number;
  errors: string[];
}

// ── Priority / Status metadata ─────────────────────────────────────────────────

export const PRIORITY_BADGE: Record<TestCasePriority, string> = {
  CRITICAL: 'bg-red-100 text-red-800',
  HIGH: 'bg-orange-100 text-orange-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  LOW: 'bg-gray-100 text-gray-700',
};

export const STATUS_BADGE: Record<TestCaseStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-800',
  DEPRECATED: 'bg-red-100 text-red-700',
};

export const INSTANCE_STATUS_BADGE: Record<TestInstanceStatus, string> = {
  NOT_RUN: 'bg-gray-100 text-gray-600',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  PASSED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  PARTIAL: 'bg-orange-100 text-orange-800',
  BLOCKED: 'bg-purple-100 text-purple-800',
};

export const TEST_MODULES = [
  { id: 'MOD-AUTH', label: 'Authentification & Identité' },
  { id: 'MOD-ACCOUNT', label: 'Espace Client' },
  { id: 'MOD-BOOKING-PIERCING', label: 'Réservation Piercing' },
  { id: 'MOD-BOOKING-TATTOO', label: 'Réservation Tatouage' },
  { id: 'MOD-PAYMENT', label: 'Paiement' },
  { id: 'MOD-PLANNING', label: 'Planning & Agenda' },
  { id: 'MOD-SHOP', label: 'Gestion Boutique' },
  { id: 'MOD-DOCS', label: 'Documents & Contrats' },
  { id: 'MOD-ADMIN', label: 'Administration' },
  { id: 'MOD-NOTIF', label: 'Notifications' },
];

export const TEST_ACTORS = [
  { id: 'ACT-VISITOR', label: 'Visiteur (non authentifié)' },
  { id: 'ACT-CLIENT', label: 'Client authentifié' },
  { id: 'ACT-SUPER_ADMIN', label: 'Super Administrateur' },
  { id: 'ACT-ADMIN', label: 'Administrateur' },
  { id: 'ACT-SHOP_MANAGER', label: 'Responsable boutique' },
  { id: 'ACT-COMPANY_MANAGER', label: 'Responsable entreprise' },
  { id: 'ACT-EMPLOYEE_VENDOR', label: 'Employé / Vendeur' },
  { id: 'ACT-PRACTITIONER', label: 'Praticien' },
];

// ── API functions ─────────────────────────────────────────────────────────────

async function handleResponse<T>(res: Response): Promise<T> {
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data.data as T;
}

// Test Cases

export async function getTestCases(query?: TestCaseListQuery): Promise<TestCase[]> {
  const params = new URLSearchParams();
  if (query?.moduleId) params.set('moduleId', query.moduleId);
  if (query?.status) params.set('status', query.status);
  if (query?.priority) params.set('priority', query.priority);
  if (query?.primaryActor) params.set('primaryActor', query.primaryActor);
  if (query?.tag) params.set('tag', query.tag);
  if (query?.search) params.set('search', query.search);

  const res = await apiClient(`/api/test-cases?${params}`);
  const data = await handleResponse<{ testCases: TestCase[] }>(res);
  return data.testCases;
}

export async function getTestCase(id: string): Promise<TestCase> {
  const res = await apiClient(`/api/test-cases/${id}`);
  const data = await handleResponse<{ testCase: TestCase }>(res);
  return data.testCase;
}

export async function createTestCase(payload: Partial<TestCase>): Promise<TestCase> {
  const res = await apiClient('/api/test-cases', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleResponse<{ testCase: TestCase }>(res);
  return data.testCase;
}

export async function updateTestCase(id: string, payload: Partial<TestCase>): Promise<TestCase> {
  const res = await apiClient(`/api/test-cases/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await handleResponse<{ testCase: TestCase }>(res);
  return data.testCase;
}

export async function deleteTestCase(id: string): Promise<void> {
  const res = await apiClient(`/api/test-cases/${id}`, { method: 'DELETE' });
  await handleResponse(res);
}

export async function importTestCases(payload: { testCases: Partial<TestCase>[] }): Promise<ImportResult> {
  const res = await apiClient('/api/test-cases/import/json', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleResponse<ImportResult>(res);
  return data;
}

export async function getTestStats(): Promise<any> {
  const res = await apiClient('/api/test-cases/stats');
  const data = await handleResponse<any>(res);
  return data;
}

// Test Case Instances

export async function getInstances(query?: InstanceListQuery): Promise<TestCaseInstance[]> {
  const params = new URLSearchParams();
  if (query?.testCaseId) params.set('testCaseId', query.testCaseId);
  if (query?.status) params.set('status', query.status);
  if (query?.environment) params.set('environment', query.environment);

  const res = await apiClient(`/api/test-cases/instances/list?${params}`);
  const data = await handleResponse<{ instances: TestCaseInstance[] }>(res);
  return data.instances;
}

export async function getInstance(id: string): Promise<TestCaseInstance> {
  const res = await apiClient(`/api/test-cases/instances/${id}`);
  const data = await handleResponse<{ instance: TestCaseInstance }>(res);
  return data.instance;
}

export async function createInstance(payload: Partial<TestCaseInstance>): Promise<TestCaseInstance> {
  const res = await apiClient('/api/test-cases/instances', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleResponse<{ instance: TestCaseInstance }>(res);
  return data.instance;
}

export async function updateInstance(id: string, payload: Partial<TestCaseInstance>): Promise<TestCaseInstance> {
  const res = await apiClient(`/api/test-cases/instances/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await handleResponse<{ instance: TestCaseInstance }>(res);
  return data.instance;
}

export async function deleteInstance(id: string, options?: { hardDelete?: boolean }): Promise<void> {
  const qs = options?.hardDelete ? '?hardDelete=true' : '';
  const res = await apiClient(`/api/test-cases/instances/${id}${qs}`, { method: 'DELETE' });
  await handleResponse(res);
}
